import { Calendar, MapPin, Edit, Trash2, Eye, Heart, ExternalLink } from 'lucide-react';
import { donationPageUrl } from '@/lib/utils/site';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Donation } from '@/lib/api/types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { PermissionGuard } from '@/components/guards/PermissionGuard';

interface DonationListItemProps {
  donation: Donation;
  onView: (donation: Donation) => void;
  onEdit: (donation: Donation) => void;
  onDelete: (donation: Donation) => void;
}

export function DonationListItem({ donation, onView, onEdit, onDelete }: DonationListItemProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden hover:shadow-md transition-all duration-200 border-border/50 rounded-lg">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Cover Image */}
            <div className="w-full sm:w-48 h-48 sm:h-32 flex-shrink-0">
              {donation.imageUrl ? (
                <img
                  src={donation.imageUrl}
                  alt={donation.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-rose-500/10 to-rose-500/5 flex items-center justify-center">
                  <Heart className="w-12 h-12 text-rose-500/40" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 line-clamp-2">
                    {donation.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {donation.description}
                  </p>
                </div>
              </div>

              {/* Meta Information */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span className="whitespace-nowrap">{format(new Date(donation.date), 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate max-w-[150px] sm:max-w-none">{donation.location}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(donation)}
                    className="h-7 px-3 text-xs"
                  >
                    <Eye className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">View</span>
                  </Button>
                  {donation.slug && (
                    <Button variant="outline" size="sm" asChild className="h-7 px-3 text-xs">
                      <a href={donationPageUrl(donation.slug)} target="_blank" rel="noopener noreferrer" title="Open the public page">
                        <ExternalLink className="w-3 h-3 sm:mr-1" />
                        <span className="hidden sm:inline">Website</span>
                      </a>
                    </Button>
                  )}
                  <PermissionGuard resource="donations" action="update">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(donation)}
                      className="h-7 px-3 text-xs"
                    >
                      <Edit className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard resource="donations" action="delete">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(donation)}
                      className="h-7 px-3 text-xs text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
